import { useMemo } from 'react'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import ArticleSection from 'components/article/article-section/ArticleSection'
import type { BlogProps, SelectProps } from './types'
import styles from './Blog.module.scss'
import { Header, Select } from '@boltenergy-be/design-system'
import { useTranslation } from 'next-i18next/pages'
import { routes } from 'utils/routes'

const Blog = ({ categories, articles }: BlogProps) => {
  // Next router
  const router = useRouter()
  const { locale, query } = router
  const categoryToShow = query?.c

  // i18n
  const { t } = useTranslation(['header', 'common', 'blog'])

  // Filter articles & categories
  const filteredArticles = useMemo(
    () => (categoryToShow ? articles.filter((article) => article.category?.slug === categoryToShow) : articles),
    [articles, categoryToShow]
  )
  const filteredCategories = useMemo(
    () => categories.filter((category) => articles.some((article) => article.category?.slug === category.slug)),
    [categories, articles]
  )

  // Set the category in the URL search params
  const handleChange: SelectProps['onChange'] = (e) => {
    const value = e.target.value

    if (value === 'ALL') {
      router.push({ pathname: routes(locale).blog, query: { c: '' } }, undefined, { shallow: true })
    } else {
      router.push({ pathname: routes(locale).blog, query: { c: value } }, undefined, { shallow: true })
    }
  }

  return (
    <main>
      <Header docked title={t('navigation.news')} color="accent-soft" shape="s3">
        <p className={styles.subheader}>{t('blog:followNews')}</p>
      </Header>
      {!!articles?.length && (
        <>
          {/* CATEGORIES */}
          <section className={classNames('section', styles.categories)}>
            <div className="container">
              <Select hiddenLabel label={t('blog:filterNews')} onChange={handleChange}>
                <option value="ALL" selected={!categoryToShow}>
                  {t('common:allCategories')}
                </option>
                {filteredCategories?.map((cat) => (
                  <option key={cat.id} value={cat.slug} selected={cat.slug === categoryToShow}>
                    {cat.title}
                  </option>
                ))}
              </Select>
            </div>
          </section>

          {/* ARTICLES */}
          <ArticleSection paginate={!categoryToShow} articles={filteredArticles} className={styles.articles} />
        </>
      )}
    </main>
  )
}

export default Blog
